/**
 * Cross-boss mechanic tuning (docs/02). Per-boss numbers stay next to their
 * boss; shared rules (gates, failsafes, readable windows) live here.
 */
export const BOSS_MECHANICS = {
  /**
   * Stall failsafe for summon gates (reinforcement elites, Hive flies,
   * Admiral escorts): a gate held longer than this (s) drops on its own, so
   * an unreachable summon can never soft-lock a fight (like the 75 s wave
   * failsafe in config/waves.ts).
   */
  summonGateFailsafe: 45,

  /**
   * Centipede anti-stall (sector sim D7: with fresh builds the last 1-3 fast
   * nodes regrew about as fast as auto-aim could chip them, 5+ min loops).
   */
  centipede: {
    /** Most nodes regrown per fight */
    regrowMax: 4,
    /** No regrowth once the whole boss is down to this many nodes */
    regrowMinNodes: 4,
    /** ...or once its total hp share is at/below this */
    regrowStopHpPct: 0.3,
    /** Short chains still FIRE faster, but their move bonus is capped so
     *  the last nodes stay hittable for auto-aim (it has no lead) */
    shortMoveCap: 0.15,
  },

  /**
   * Webspinner SILK COCOON (docs/02 wave 4, signature at every tier): silk
   * panels wrap the body. A hit lands on the panel facing it — intact silk
   * catches the shot (the panel loses that much silk) and lets only
   * `through` of it reach the body. Shot-apart panels gape open, then knit
   * back: threads cross the hole while the silk regrows.
   */
  webspinner: {
    panels: 8,
    /** Silk per panel as a share of the boss max HP */
    panelHp: 0.025,
    /** Share of a hit that reaches the body through intact silk */
    through: 0.35,
    /** A torn panel gapes this long (s) before it starts knitting */
    openTime: 6,
    /** Knitting regrows a full panel over this long (s) */
    knitTime: 1.2,
  },

  /**
   * Singularity EVENT HORIZON (docs/02 wave 4, signature at every tier):
   * while it holds, player shots that enter the accretion disk are pulled
   * into orbit and spiral down into the core — swallowed, no damage. Then it
   * OPENS: the disk collapses and the bare core takes hits for a clear
   * window. The disk flares and flickers before it opens and fades back in
   * before it closes.
   */
  singularity: {
    /** Seconds the horizon holds (includes `warn`) */
    closed: 4.0,
    /** Opening tell at the end of `closed` (s) */
    warn: 0.8,
    /** Clear window: the core is exposed (s) */
    open: 3.6,
    /** The disk fades back in this long before the window ends (s) */
    closeWarn: 0.6,
    /** Accretion disk radius (px): shots inside it are captured */
    captureR: 210,
    /** A captured shot whirls at this speed (px/s) ... */
    orbitSpeed: 420,
    /** ... while it sinks toward the core (px/s) */
    sink: 95,
  },
} as const;
